import React, { useState } from 'react';
import { Modal, Input, Button, Typography, Space, Alert, message } from 'antd';
import { WhatsAppOutlined, SendOutlined } from '@ant-design/icons';
import { getFunctions, httpsCallable } from 'firebase/functions';

const { Text, Title } = Typography;
const { TextArea } = Input;

const WhatsAppModal = ({ 
  visible, 
  onCancel, 
  documentData, 
  documentType, // 'invoice' or 'quotation'
  onSendSuccess 
}) => {
  const [customMessage, setCustomMessage] = useState('');
  const [sending, setSending] = useState(false);

  // Format phone number for display
  const formatPhoneForDisplay = (phoneNumber) => {
    if (!phoneNumber) return 'N/A';
    const cleanNumber = phoneNumber.replace(/\D/g, '');
    return `+65${cleanNumber}`;
  };

  // Generate default message
  const getDefaultMessage = () => {
    const docNumber = documentType === 'invoice' 
      ? documentData?.invoiceNumber 
      : documentData?.quotationNumber;
    
    const contactName = documentData?.attn || 'there';
    const docName = documentType === 'invoice' ? 'invoice' : 'quotation';
    
    return `Hi ${contactName}, please find attached your ${docName} ${docNumber}. Thank you for your business!`;
  };

  const handleSend = async () => {
    if (!documentData?.tel) {
      message.error('No phone number available for this document');
      return;
    }

    setSending(true);
    
    try {
      const functions = getFunctions();
      const sendWhatsApp = httpsCallable(functions, 'sendWhatsApp');
      
      const messageText = customMessage.trim() || getDefaultMessage();
      
      const result = await sendWhatsApp({
        documentType: documentType,
        documentId: documentData.id,
        phoneNumber: documentData.tel,
        customMessage: messageText
      });

      console.log('WhatsApp sent successfully:', result.data);
      
      message.success(`${documentType} sent via WhatsApp successfully!`);
      
      // Call success callback to refresh data
      if (onSendSuccess) {
        onSendSuccess();
      }
      
      // Close modal
      onCancel();
      
    } catch (error) {
      console.error('Error sending WhatsApp:', error);
      
      let errorMessage = 'Failed to send WhatsApp message';
      
      if (error.code === 'functions/invalid-argument') {
        errorMessage = 'Invalid phone number or document data';
      } else if (error.code === 'functions/not-found') {
        errorMessage = 'Document not found';
      } else if (error.code === 'functions/unauthenticated') {
        errorMessage = 'Authentication required';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      message.error(errorMessage);
    } finally {
      setSending(false);
    }
  };

  const handleCancel = () => {
    setCustomMessage('');
    onCancel();
  };

  if (!documentData) {
    return null;
  }

  const formattedPhone = formatPhoneForDisplay(documentData.tel);
  const docNumber = documentType === 'invoice' 
    ? documentData.invoiceNumber 
    : documentData.quotationNumber;

  return (
    <Modal
      title={
        <Space>
          <WhatsAppOutlined style={{ color: '#25D366' }} />
          <span>Send via WhatsApp</span>
        </Space>
      }
      open={visible}
      onCancel={handleCancel}
      width={600}
      footer={[
        <Button key="cancel" onClick={handleCancel} disabled={sending}>
          Cancel
        </Button>,
        <Button
          key="send"
          type="primary"
          icon={<SendOutlined />}
          onClick={handleSend}
          loading={sending}
          style={{ backgroundColor: '#25D366', borderColor: '#25D366' }}
        >
          Send WhatsApp
        </Button>
      ]}
    >
      <div style={{ padding: '16px 0' }}>
        <div style={{ marginBottom: 16 }}>
          <Text strong>To: </Text>
          <Text>{formattedPhone}</Text>
          <br />
          <Text type="secondary">
            {documentData.companyName} - {documentData.attn}
          </Text>
        </div>

        <div style={{ marginBottom: 16 }}>
          <Text strong>Document: </Text>
          <Text>
            {documentType === 'invoice' ? 'Invoice' : 'Quotation'} {docNumber}
          </Text>
        </div>

        <div style={{ marginBottom: 16 }}>
          <Title level={5}>Message:</Title>
          <TextArea
            value={customMessage}
            onChange={(e) => setCustomMessage(e.target.value)}
            placeholder={getDefaultMessage()}
            rows={4}
            maxLength={500}
            showCount
          />
          <Text type="secondary" style={{ fontSize: '12px', marginTop: '4px' }}>
            Leave empty to use default message
          </Text>
        </div>

        {!documentData.tel && (
          <Alert
            message="No phone number available"
            description="This document doesn't have a phone number associated with it."
            type="warning"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}

        <div style={{ marginTop: 16, padding: 12, backgroundColor: '#f5f5f5', borderRadius: 6 }}>
          <Text strong>Preview message:</Text>
          <div style={{ 
            marginTop: 8, 
            padding: 8, 
            backgroundColor: 'white', 
            borderRadius: 4,
            fontFamily: 'monospace',
            fontSize: '12px'
          }}>
            {customMessage.trim() || getDefaultMessage()}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default WhatsAppModal;
